import React, { Fragment, useState } from "react";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useTranslation } from "react-i18next";
import ActionRow from "../../../admin/clinic/components/actions/ActionRow";
import useFileDownload from "../../../../hooks/useFileDownload";
import useFileViewer from "../../../../hooks/useFileViewer";
import FileViewerDialog from "../../../../components/display/fileViewer/FileViewerDialog";
import { DOWNLOAD_INVOICE } from "../../../../enums/ApiPaths";
import {
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

const INVOICE_FILE_NAME = "invoice.pdf";
// Which action the invoice picker dialog performs on the selected invoice
const INVOICE_ACTION = { VIEW: "view", DOWNLOAD: "download" };

export default function DownloadInvoice({ invoicesList }) {
  const { t } = useTranslation();
  const { downloadInvoice } = useFileDownload(DOWNLOAD_INVOICE);
  const {
    viewFile,
    downloadViewerFile,
    closeViewer,
    viewerFile,
    isPathLoading,
  } = useFileViewer();

  // null when the picker dialog is closed, otherwise the pending INVOICE_ACTION
  const [pickerAction, setPickerAction] = useState(null);

  if (!invoicesList || invoicesList.length === 0) return null;

  const hasSingleInvoice = invoicesList.length === 1;
  const invoicePath = (id) => `${DOWNLOAD_INVOICE}${id}`;

  const viewInvoice = (id) =>
    viewFile({
      path: invoicePath(id),
      name: INVOICE_FILE_NAME,
      mimeType: "application/pdf",
    });

  const runAction = (action, id) => {
    if (action === INVOICE_ACTION.VIEW) {
      viewInvoice(id);
    } else {
      downloadInvoice(id);
    }
  };

  const handleClick = (action) => {
    if (hasSingleInvoice) {
      runAction(action, invoicesList[0].id);
    } else {
      setPickerAction(action);
    }
  };

  const handleClose = () => setPickerAction(null);

  const handleSelectInvoice = (id) => {
    const action = pickerAction;
    handleClose();
    runAction(action, id);
  };

  return (
    <Fragment>
      <Divider />
      <ActionRow
        justButton
        icon={<VisibilityIcon />}
        label={t("ViewInvoice")}
        description={t("ViewInvoiceDesc")}
        loading={invoicesList.some(({ id }) => isPathLoading(invoicePath(id)))}
        onClick={() => handleClick(INVOICE_ACTION.VIEW)}
        testId={"appointment.details.view-invoice-button"}
      />
      <Divider />
      <ActionRow
        justButton
        icon={<ReceiptLongIcon />}
        label={t("DownloadInvoice")}
        description={t("DownloadInvoiceDesc")}
        onClick={() => handleClick(INVOICE_ACTION.DOWNLOAD)}
        testId={"appointment.details.download-invoice-button"}
      />
      <Dialog
        open={pickerAction !== null}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Stack spacing={0.25}>
            <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
              {t(
                pickerAction === INVOICE_ACTION.VIEW
                  ? "ViewInvoice"
                  : "DownloadInvoice"
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("InvoicesAvailable")} {": "} {invoicesList.length}
            </Typography>
          </Stack>
          <Tooltip title={t("Close")}>
            <IconButton onClick={handleClose} size="small" edge="end">
              <CloseIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ px: 1, pb: 2 }}>
          <List disablePadding>
            {invoicesList.map(({ id, generatedAt }, index) => (
              <ListItemButton
                key={id}
                onClick={() => handleSelectInvoice(id)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <DescriptionOutlinedIcon color="action" fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={`#${index + 1}`}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  secondary={generatedAt}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      <FileViewerDialog
        file={viewerFile}
        onClose={closeViewer}
        onDownload={downloadViewerFile}
      />
    </Fragment>
  );
}
